import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import Label from "../../../../components/form_fields/Label";
import OrangeButton from "../../../../components/buttons/OrangeButton";
import ModalComponent from "../../../../components/modals/ModalFormComponent";
import { TimeRequestKind } from "../data";
import { useTimeRequestMutations } from "../useTimeRequestApi";

type RevokeTimeRequestModalProps = {
	kind: TimeRequestKind;
	request: any;
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	onRevoked: () => void;
};

const RevokeTimeRequestModal = ({
	kind,
	request,
	open,
	setOpen,
	onRevoked,
}: RevokeTimeRequestModalProps) => {
	const [saving, setSaving] = useState(false);
	const { revoke } = useTimeRequestMutations(kind);
	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<{ reason: string }>();

	const handleClose = () => {
		setOpen(false);
		reset();
	};

	const onSubmit: SubmitHandler<{ reason: string }> = ({ reason }) => {
		setSaving(true);
		revoke.mutate(
			{ id: request.id, payload: { reason } },
			{
				onSuccess: () => {
					onRevoked();
					handleClose();
				},
				onSettled: () => setSaving(false),
			},
		);
	};

	return (
		<ModalComponent
			width={600}
			open={open}
			title="Revoke Approved Request"
			handleClose={handleClose}
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<p className="text-2sm text-gray-600">
					Revoking removes this {kind === "workTime" ? "work time" : "overtime"} from
					payroll. It can't be undone, and isn't possible once the pay period is closed.
				</p>
				<div className="w-full mt-6">
					<Label title="Reason" for="reason" />
					<textarea
						id="reason"
						rows={3}
						placeholder="Why is this request being revoked?"
						className={`w-full bg-secondary-gray rounded-lg px-5 py-4 mt-2 text-sm ${
							errors.reason
								? "border border-red-500 focus:border-red-500 focus:outline-red-500"
								: "border-none"
						}`}
						{...register("reason", {
							validate: (value) => !!value?.trim() || "Enter the reason",
						})}
					/>
					{errors.reason && (
						<p className="text-red-500 text-sm mt-1">{errors.reason.message}</p>
					)}
				</div>
				<div className="w-full flex justify-end mt-8">
					<OrangeButton
						type="submit"
						title={saving ? "Revoking..." : "Revoke Request"}
						className="px-8"
						disabled={saving}
					/>
				</div>
			</form>
		</ModalComponent>
	);
};

export default RevokeTimeRequestModal;
